import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  LockKeyhole,
  ShieldCheck,
  Smartphone,
  UserRound,
} from "lucide-react";
import "./LoginPage.css";

export default function Home() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMobileDevice = /iphone|ipad|ipod|android|blackberry|windows phone/.test(userAgent);
    setIsMobile(isMobileDevice);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post("/api/auth/login", { identifier, password });
      localStorage.setItem("token", data.token);

      const { slug, user_name, user_id, permissions, dealer_mode } = jwtDecode(data.token);
      localStorage.setItem("user_name", user_name);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("permission", permissions || "");
      localStorage.setItem("dealer_mode", dealer_mode || 0);

      if (dealer_mode === 2) {
        localStorage.setItem("dealerTable", null);
        navigate(`/${slug}/dealerdashboard`);
      } else if (String(permissions || "").includes("Y")) {
        navigate(`/${slug}/tournamentsHome`);
      } else {
        navigate(`/${slug}/game`);
      }
    } catch (err) {
      if (err.response?.data?.membershipRequired) {
        const renewalCardroom = err.response.data.cardroom || "";
        navigate(
          `/renew-membership?cardroom=${encodeURIComponent(renewalCardroom)}`,
        );
        return;
      }
      setError(err.response?.data?.error || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="nexus-login">
      <div className="nexus-login__ambient" aria-hidden="true">
        <div className="nexus-login__matrix">
          {Array.from({ length: 42 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>

      <Link className="nexus-login__back" to="/">
        <ArrowLeft size={17} aria-hidden="true" />
        Nexus Game Engine
      </Link>

      <section className="nexus-login__shell" aria-label="Nexus login">
        <div className="nexus-login__card">
          <div className="nexus-login__brand nexus-login__brand--card">
            <span>N</span>
            <div>
              <strong>Nexus Game Engine</strong>
              <small>Administrative command surface</small>
            </div>
          </div>

          <div className="nexus-login__card-head">
            <p className="nexus-login__eyebrow">Secure login</p>
            <h1>Welcome back</h1>
          </div>

          <form onSubmit={onSubmit} className="nexus-login__form">
            <label className="nexus-login__field">
              <span>Username or Email</span>
              <div>
                <UserRound size={18} aria-hidden="true" />
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>
            </label>

            <label className="nexus-login__field">
              <span>Password</span>
              <div>
                <LockKeyhole size={18} aria-hidden="true" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
            </label>

            {error && <div className="nexus-login__error">{error}</div>}

            <button
              type="submit"
              disabled={loading}
              className="nexus-login__submit"
            >
              {loading ? "Signing in..." : "Log in"}
            </button>
          </form>

          {isMobile && (
            <div className="nexus-login__mobile-note">
              <Smartphone size={18} aria-hidden="true" />
              <span>Mobile-ready workspace. Add Nexus to your home screen from your browser menu.</span>
            </div>
          )}
        </div>

        <aside className="nexus-login__panel" aria-label="Workspace status">
          <div className="nexus-login__brief">
            <p className="nexus-login__eyebrow">Private organization access</p>
            <h2>Workspace access for organization teams.</h2>
            <p>
              Continue into your member operations, staff workflows, and
              administrative ledgering environment. Active subscriptions keep
              workspace access open, with Pro usage based on members who played
              during the month.
            </p>
          </div>

          <div className="nexus-login__signals">
            <span>
              <ShieldCheck size={17} aria-hidden="true" />
              Secure Invite Links
            </span>
            <span>
              <Building2 size={17} aria-hidden="true" />
              Staff access controls
            </span>
            <span>
              <LockKeyhole size={17} aria-hidden="true" />
              Protected Administrative Data
            </span>
          </div>
        </aside>
      </section>
    </main>
  );
}
